/**
 * Canvas vs WebGL 벡터 렌더 실측 (dev 전용)
 *
 * 사용 (site 로드 후 DevTools 콘솔):
 *   await __panelRendererBenchmark()
 *   await __panelRendererBenchmark({ panSteps: 40, includeTiles: true })
 *
 * 콘솔 필터: panel-benchmark
 */
import { createPanelPlanningMapHost } from '../map/createPanelPlanningMapHost.js';
import {
  summarizeRenderSamples,
  cloneVectorSourceFeatures
} from './panelMapRendererBenchmarkStats.js';

const NS = '[panel-benchmark]';
const BENCH_TARGET_ID = 'panel-renderer-benchmark-host';

function ensureBenchmarkHostElement(width, height) {
  let el = document.getElementById(BENCH_TARGET_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = BENCH_TARGET_ID;
    el.setAttribute('aria-hidden', 'true');
    Object.assign(el.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: `${width}px`,
      height: `${height}px`,
      visibility: 'hidden',
      pointerEvents: 'none'
    });
    document.body.appendChild(el);
  }
  el.style.width = `${width}px`;
  el.style.height = `${height}px`;
  return el;
}

function removeBenchmarkHostElement() {
  document.getElementById(BENCH_TARGET_ID)?.remove();
}

/**
 * @param {object} params
 * @param {'canvas'|'webgl'} params.renderer
 * @param {import('ol/Feature').default[]} params.features
 * @param {[number, number]} params.center
 * @param {number} params.zoom
 * @param {(feature: import('ol/Feature').default) => import('ol/style/Style').default|import('ol/style/Style').default[]|void} [params.styleFn]
 * @param {boolean} [params.includeTiles=false]
 * @param {number} [params.width=960]
 * @param {number} [params.height=640]
 */
export function createBenchmarkMapHost({
  renderer,
  features,
  center,
  zoom,
  styleFn,
  includeTiles = false,
  width = 960,
  height = 640
}) {
  ensureBenchmarkHostElement(width, height);
  const host = createPanelPlanningMapHost(BENCH_TARGET_ID, {
    center,
    zoom,
    attachCursorPlugin: false,
    renderer
  });

  const map = host.getMap();
  const vectorSource = host.getVectorSource();
  vectorSource.clear();
  vectorSource.addFeatures(features.map(f => f.clone()));

  const vectorLayer = host.getVectorLayer();
  if (renderer === 'canvas' && typeof styleFn === 'function') {
    vectorLayer.setStyle(feature => styleFn(feature));
  }

  if (!includeTiles) {
    host.getLayer('satellite')?.setVisible?.(false);
    host.getLayer('siteImage')?.setVisible?.(false);
  }

  map.updateSize();
  return host;
}

/**
 * @param {object} params
 * @param {import('ol/Map').default} params.map
 * @param {import('ol/layer/Vector').default|import('ol/layer/WebGLVector').default} params.vectorLayer
 * @param {number} [params.panSteps=30]
 */
export async function sampleVectorLayerRenderMs({ map, vectorLayer, panSteps = 30 }) {
  /** @type {number[]} */
  const samples = [];
  let renderStart = 0;

  const onPreRender = () => {
    renderStart = performance.now();
  };
  const onPostRender = () => {
    if (renderStart > 0) {
      samples.push(performance.now() - renderStart);
    }
  };

  vectorLayer.on('prerender', onPreRender);
  vectorLayer.on('postrender', onPostRender);

  const view = map.getView();
  const baseCenter = view.getCenter();
  const resolution = view.getResolution() ?? 1;

  try {
    for (let i = 0; i < panSteps; i += 1) {
      await new Promise(resolve => {
        map.once('rendercomplete', resolve);
        const offset = resolution * (8 + (i % 5) * 3);
        const sign = i % 2 === 0 ? 1 : -1;
        view.setCenter([baseCenter[0] + sign * offset, baseCenter[1] + sign * offset * 0.3]);
        map.renderSync();
      });
    }

    // idle 3 frames (pointermove 유사 — repaint만)
    for (let i = 0; i < 3; i += 1) {
      await new Promise(resolve => {
        map.once('rendercomplete', resolve);
        map.renderSync();
      });
    }
  } finally {
    vectorLayer.un('prerender', onPreRender);
    vectorLayer.un('postrender', onPostRender);
  }

  return summarizeRenderSamples(samples);
}

function logBenchmarkTable(label, canvasStats, webglStats) {
  const speedupMean =
    canvasStats.mean > 0 ? Number((canvasStats.mean / Math.max(webglStats.mean, 0.01)).toFixed(2)) : null;
  const speedupP95 =
    canvasStats.p95 > 0 ? Number((canvasStats.p95 / Math.max(webglStats.p95, 0.01)).toFixed(2)) : null;

  console.log(NS, label, {
    canvasMs: canvasStats,
    webglMs: webglStats,
    speedupMean,
    speedupP95,
    savedMeanMs: Number((canvasStats.mean - webglStats.mean).toFixed(2)),
    savedP95Ms: Number((canvasStats.p95 - webglStats.p95).toFixed(2))
  });

  if (typeof console.table === 'function') {
    console.table([
      { renderer: 'canvas', ...canvasStats },
      { renderer: 'webgl', ...webglStats },
      {
        renderer: 'speedup (canvas/webgl)',
        mean: speedupMean,
        p50: canvasStats.p50 / Math.max(webglStats.p50, 0.01),
        p95: speedupP95,
        max: canvasStats.max / Math.max(webglStats.max, 0.01),
        count: '-'
      }
    ]);
  }

  return { canvas: canvasStats, webgl: webglStats, speedupMean, speedupP95 };
}

/**
 * @param {object} params
 * @param {import('ol/Map').default} params.map
 * @param {import('ol/source/Vector').default} params.vectorSource
 * @param {import('ol/layer/Vector').default} params.vectorLayer
 * @param {(feature: import('ol/Feature').default) => unknown} [params.styleFn]
 * @param {number} [params.panSteps=30]
 * @param {boolean} [params.includeTiles=false]
 * @param {number} [params.width]
 * @param {number} [params.height]
 */
export async function runPanelRendererCompare({
  map,
  vectorSource,
  styleFn,
  panSteps = 30,
  includeTiles = false,
  width,
  height
}) {
  if (!map || !vectorSource) {
    throw new Error('map and vectorSource are required');
  }

  const view = map.getView();
  const center = view.getCenter();
  const zoom = view.getZoom();
  const featureClones = cloneVectorSourceFeatures(vectorSource);
  const viewport = map.getViewport?.();
  const benchWidth = width ?? viewport?.clientWidth ?? 960;
  const benchHeight = height ?? viewport?.clientHeight ?? 640;

  console.log(NS, 'start', {
    featureCount: featureClones.length,
    center,
    zoom,
    panSteps,
    includeTiles,
    benchSize: [benchWidth, benchHeight]
  });

  const tCanvas = performance.now();
  const canvasHost = createBenchmarkMapHost({
    renderer: 'canvas',
    features: featureClones,
    center,
    zoom,
    styleFn,
    includeTiles,
    width: benchWidth,
    height: benchHeight
  });
  const canvasStats = await sampleVectorLayerRenderMs({
    map: canvasHost.getMap(),
    vectorLayer: canvasHost.getVectorLayer(),
    panSteps
  });
  canvasHost.destroy();
  const canvasTotalMs = performance.now() - tCanvas;

  const tWebgl = performance.now();
  const webglHost = createBenchmarkMapHost({
    renderer: 'webgl',
    features: featureClones,
    center,
    zoom,
    includeTiles,
    width: benchWidth,
    height: benchHeight
  });
  const webglStats = await sampleVectorLayerRenderMs({
    map: webglHost.getMap(),
    vectorLayer: webglHost.getVectorLayer(),
    panSteps
  });
  webglHost.destroy();
  const webglTotalMs = performance.now() - tWebgl;

  removeBenchmarkHostElement();

  const result = logBenchmarkTable(
    includeTiles ? 'vector+tile layer postrender (ms)' : 'vector layer postrender (ms)',
    canvasStats,
    webglStats
  );

  console.log(NS, 'wallClock', {
    canvasBenchTotalMs: Number(canvasTotalMs.toFixed(1)),
    webglBenchTotalMs: Number(webglTotalMs.toFixed(1))
  });

  return { ...result, featureCount: featureClones.length, panSteps, includeTiles };
}

export function installPanelRendererBenchmarkGlobal(runCompare) {
  if (typeof window === 'undefined' || typeof runCompare !== 'function') {
    return;
  }
  window.__panelRendererBenchmark = runCompare;
  console.log(NS, 'ready → await __panelRendererBenchmark()');
}
